import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../../store/authStore';
import { useEventStore } from '../../../store/eventStore';
import {
  CalendarIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  UserIcon,
  PhoneIcon,
  ClockIcon
} from "@heroicons/react/24/outline";
import ReservationDetailModal from './ReservationDetailModal';

export default function EventReservation() {
  const { user } = useAuthStore();
  const { getReservations, isLoading, error } = useEventStore();
  const isMountedRef = useRef(false);

  // 상태 관리
  const [reservations, setReservations] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchText, setSearchText] = useState(""); // 대표자명 검색 필드 추가
  const [localLoading, setLocalLoading] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);

  // 페이지 로드 시 오늘 날짜로 설정
  useEffect(() => {
    isMountedRef.current = true;
    const controller = new AbortController();
    let today = new Date().toISOString().split("T")[0];
    // today = '2023-10-09'
    setStartDate(today);
    setEndDate(today);

    handleSearch(today, today, { signal: controller.signal });

    return () => {
      isMountedRef.current = false;
      controller.abort();
    };
  }, []);

  // 날짜 필터 초기화
  const handleClearDate = (type) => {
    if (type === "start") {
      setStartDate("");
    } else {
      setEndDate("");
    }
  };

  // 검색 버튼 클릭 함수
  const handleSearch = async (_startDate, _endDate, options = {}) => {
    const { signal } = options;
    if (!_startDate || !_endDate) {
      alert('시작일과 종료일을 모두 선택해주세요.');
      return;
    }

    if (signal?.aborted || !isMountedRef.current) return;
    setLocalLoading(true);

    try {
      // API를 통해 예약 정보 가져오기
      const response = await getReservations(_startDate, _endDate, searchText, {
        signal,
      });
      if (signal?.aborted || !isMountedRef.current) return;

      if (response && response.data) {
        setReservations(response.data);
      } else {
        if (signal?.aborted || !isMountedRef.current) return;
        alert(response.error || "예약 정보를 가져오는 중 오류가 발생했습니다.");
      }
    } catch (err) {
      const isAbortError =
        err?.name === "AbortError" || err?.code === "ERR_CANCELED";
      if (isAbortError || signal?.aborted || !isMountedRef.current) return;
      console.error("예약 정보 검색 중 오류 발생:", err);
      alert("예약 정보를 가져오는 중 오류가 발생했습니다.");
    } finally {
      if (!signal?.aborted && isMountedRef.current) {
        setLocalLoading(false);
      }
    }
  };

  // 예약 상세 정보 표시 함수
  const handleViewDetail = (reservation) => {
    // 선택한 예약 정보 저장
    setSelectedReservation(reservation);
    // 모달 열기
    setIsDetailModalOpen(true);
  };
  
  // 모달 닫기 함수
  const handleCloseModal = () => {
    setIsDetailModalOpen(false);
    setSelectedReservation(null);
  };

  return (
    <div className="py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">예약건별 배정 관리</h1>
      <p className="text-gray-600 mb-4">
        예약건별 승차권 배정 관리를 조회하는 페이지입니다.
      </p>

      {/* 검색 필터 영역 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex flex-wrap items-center gap-4">
            {/* 시작 날짜 선택 */}
            <div className="relative">
              <div className="flex items-center">
                <label
                  htmlFor="startDate"
                  className="mr-2 text-sm font-medium text-gray-700"
                >
                  출발일자
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="startDate"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setEndDate(e.target.value);
                    }}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  {startDate && (
                    <button
                      onClick={() => handleClearDate("start")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 종료 날짜 선택 */}
            <div className="relative">
              <div className="flex items-center">
                <label
                  htmlFor="endDate"
                  className="mr-2 text-sm font-medium text-gray-700"
                >
                  종료일자
                </label>
                <div className="relative">
                  <input
                    type="date"
                    id="endDate"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  {endDate && (
                    <button
                      onClick={() => handleClearDate("end")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 대표자명 검색 필드 */}
            <div className="relative">
              <div className="flex items-center">
                <label
                  htmlFor="searchText"
                  className="mr-2 text-sm font-medium text-gray-700"
                >
                  대표자명
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="searchText"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="대표자명 입력"
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  {searchText && (
                    <button
                      onClick={() => setSearchText("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 검색 버튼 */}
            <button
              onClick={() => handleSearch(startDate, endDate)}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition duration-150 ease-in-out"
            >
              <div className="flex items-center">
                <MagnifyingGlassIcon className="h-4 w-4 mr-1" />
                검색하기
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* 예약 정보 테이블 */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  출발일
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  행사명
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  예약번호
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  대표자명
                </th>
                <th
                  scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  대표자번호
                </th>
                <th
                  scope="col"
                  className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  상세보기
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" id="main-tbody">
              {reservations.length > 0 ? (
                reservations.map((reservation, index) => (
                  <tr key={reservation.reservation_id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {reservation.departure_date}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="w-full">
                        {reservation.event_name || '정보 없음'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {reservation.reservation_number || '정보 없음'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-500" />
                        {reservation.representative_name || '정보 없음'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center">
                        <PhoneIcon className="h-4 w-4 mr-1 text-gray-500" />
                        {reservation.representative_phone || '정보 없음'}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-sm font-medium">
                      <button
                        onClick={() => handleViewDetail(reservation)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                        상세보기
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    {localLoading ? (
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2">로딩 중...</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <ClockIcon className="h-10 w-10 text-gray-400 mb-2" />
                        <p>조회된 예약 정보가 없습니다.</p>
                        <p className="text-sm">날짜를 선택하고 검색해 주세요.</p>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 상세 모달 */}
      <ReservationDetailModal 
        isOpen={isDetailModalOpen} 
        onClose={handleCloseModal} 
        reservationData={selectedReservation} 
      />
    </div>
  );
}
